import { eq, and, desc } from 'drizzle-orm';
import { BaseRepository } from '../../../classes/BaseRepository';
import { ehrIntegrations } from '../models/ehrIntegrations';
import type { EhrIntegration } from '../types/EhrIntegration';
import type { EhrProvider } from '../types/EhrProvider';
import { retryableQuery } from '../../../functions/retryableQuery';
import { db } from '../../../db';
import { convertToAppError, type AsyncResult, type AppError, ok, err } from '@shared/utils';
import { DATA_API_MESSAGES, HTTP_STATUS_NAMES } from '@shared/constants';

/**
 * @group EHR
 * @summary EHR Integration repository
 * 
 * <blockquote>
 * Provides data access methods for EHR integration management, including CRUD operations and patient-EHR connection management.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const ehrIntegrationRepository = new EhrIntegrationRepository();
 * const integration = await ehrIntegrationRepository.findByPatientAndProvider(patientId, 'openemr');
 * integration.match({
 *   ok: (result) => console.log('Found integration:', result),
 *   err: (error) => console.error('Error:', error.errorMessage)
 * });
 * ```
 */
export class EhrIntegrationRepository extends BaseRepository<EhrIntegration> {
  protected table = ehrIntegrations;

  /**
   * Find active integration for a patient and EHR provider
   * 
   * @param patientId - The patient ID to search for
   * @param ehrProvider - The EHR provider type
   * @returns AsyncResult containing the integration or null if not found
   */
  async findByPatientAndProvider(
    patientId: string,
    ehrProvider: EhrProvider
  ): AsyncResult<EhrIntegration | null, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<EhrIntegration[]>(async () =>
          (await database
            .select()
            .from(ehrIntegrations)
            .where(
              and(
                eq(ehrIntegrations.patientId, patientId),
                eq(ehrIntegrations.ehrProvider, ehrProvider),
                eq(ehrIntegrations.isActive, true)
              )
            )
            .limit(1)) as EhrIntegration[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from findByPatientAndProvider: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            return ok(rows[0] || null);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Find all active integrations for a patient
   * 
   * @param patientId - The patient ID to search for
   * @returns AsyncResult containing array of active integrations
   */
  async findActiveByPatient(patientId: string): AsyncResult<EhrIntegration[], AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<EhrIntegration[]>(async () =>
          (await database
            .select()
            .from(ehrIntegrations)
            .where(
              and(
                eq(ehrIntegrations.patientId, patientId),
                eq(ehrIntegrations.isActive, true)
              )
            )
            .orderBy(desc(ehrIntegrations.createdAt))) as EhrIntegration[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from findActiveByPatient: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            return ok(rows);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Find integration by external ID
   * 
   * @param externalId - The external ID to search for
   * @returns AsyncResult containing the integration or null if not found
   */
  async findByExternalId(externalId: string): AsyncResult<EhrIntegration | null, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<EhrIntegration[]>(async () =>
          (await database
            .select()
            .from(ehrIntegrations)
            .where(eq(ehrIntegrations.externalId, externalId))
            .limit(1)) as EhrIntegration[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from findByExternalId: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            return ok(rows[0] || null);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Deactivate an integration by setting isActive to false
   * 
   * @param id - The integration ID to deactivate
   * @returns AsyncResult containing void on success
   */
  async deactivateIntegration(id: string): AsyncResult<void, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<EhrIntegration[]>(async () =>
          (await database
            .update(ehrIntegrations)
            .set({ 
              isActive: false, 
              updatedAt: new Date() 
            })
            .where(eq(ehrIntegrations.id, id))
            .returning()) as EhrIntegration[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from deactivateIntegration: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            if (rows.length === 0) {
              return err(convertToAppError(
                `No integration found with id: ${id}`,
                DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
                HTTP_STATUS_NAMES.NOT_FOUND
              ));
            }

            return ok(undefined);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Update connection metadata for an integration
   * 
   * @param id - The integration ID to update
   * @param metadata - The metadata to set
   * @returns AsyncResult containing the updated integration
   */
  async updateConnectionMetadata(
    id: string, 
    metadata: Record<string, unknown>
  ): AsyncResult<EhrIntegration, AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery<EhrIntegration[]>(async () =>
          (await database
            .update(ehrIntegrations)
            .set({ 
              connectionMetadata: metadata,
              updatedAt: new Date() 
            })
            .where(eq(ehrIntegrations.id, id))
            .returning()) as EhrIntegration[]
        );

        return queryResult.match({
          ok: (rows) => {
            if (!Array.isArray(rows)) {
              return err(convertToAppError(
                "Unexpected result from updateConnectionMetadata: not an array",
                DATA_API_MESSAGES.ERRORS.INTERNAL_SERVER,
                HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
              ));
            }

            if (rows.length === 0) {
              return err(convertToAppError(
                `No integration found with id: ${id}`,
                DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
                HTTP_STATUS_NAMES.NOT_FOUND
              ));
            }

            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }
}
